import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Product IDs the Bundle & Save automatic discounts actually cover, rendered
 * into the page by snippets/bundle-eligible-product-ids.liquid via
 * layout/theme.liquid, from the same theme setting
 * (settings.bundle_eligible_collections) that snippets/cart-bundle-bar.liquid
 * reads. Needed because /cart.js line items don't carry their product's
 * collections, so eligibility can't be worked out client-side any other way.
 *
 * Returns an empty set when the list isn't on the page (nothing configured, or
 * an older layout) — callers treat that as "no eligibility data" and fall back
 * to counting the whole cart.
 *
 * @returns {Set<string>}
 */
function readBundleEligibleProductIds() {
  const element = document.getElementById('bundle-eligible-product-ids');
  if (!element?.textContent) return new Set();

  try {
    const ids = JSON.parse(element.textContent);
    return new Set((Array.isArray(ids) ? ids : []).map((id) => String(id)));
  } catch (error) {
    return new Set();
  }
}

/**
 * V2 of the Bundle & Save widget controller. Drives the "details panel" (title,
 * price, variant select, add to cart) exactly like the original, plus click-to-cycle
 * image navigation: clicking a product's image advances to its next photo, wrapping
 * back to the first after the last (an endless carousel driven entirely by click,
 * with no hover dependency so it works identically on mobile and desktop).
 *
 * The outer product-to-product carousel is still handled entirely by the theme's
 * existing <slideshow-component> — this component only listens for `slideshow:select`
 * to know which product is active, and keeps the price preview in sync with how many
 * qualifying items are already in the cart.
 *
 * "Qualifying" means covered by the Bundle & Save discounts: ineligible cart lines
 * don't move the tier, and a product the discounts don't cover never previews a
 * bundle price at all. Note this is a separate question from the
 * custom.mix_match_eligible metafield, which decides which products appear in the
 * carousel in the first place.
 *
 * @typedef {Object} BundleVariant
 * @property {number} id
 * @property {string} title
 * @property {boolean} available
 * @property {number} price
 * @property {number|null} compareAtPrice
 *
 * @typedef {Object} BundleProduct
 * @property {number} id
 * @property {string} title
 * @property {string} url
 * @property {string} [description]
 * @property {string} [sizeChartHtml] - Pre-rendered size guide markup (image and/or table) for
 *   this specific product, built server-side from the same product.metafields.custom.size_chart /
 *   size_guide_image / size_guide_image_width metafields the hoodie-uv product template and Test
 *   Block 4 read — kept as ready-to-use HTML here since this widget swaps between many products
 *   client-side and each one needs its own rendered chart on demand.
 * @property {boolean} [hasSizeChart] - Whether sizeChartHtml is non-empty for this product, so
 *   the Size Chart dropdown itself can be hidden entirely for products with no size guide
 *   metafields set, rather than showing an empty dropdown.
 * @property {BundleVariant[]} variants
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} title
 * @property {HTMLElement} price
 * @property {HTMLElement} comparePrice
 * @property {HTMLElement} badge
 * @property {HTMLElement} variantOption
 * @property {HTMLSelectElement} variantSelect
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} addToCartButtonText
 * @property {HTMLElement} [description]
 * @property {HTMLElement} [sizeChart]
 * @property {HTMLElement} [sizeChartWrapper]
 * @extends {Component<Refs>}
 */
class BundleAndSaveV2Component extends Component {
  requiredRefs = [
    'title',
    'price',
    'comparePrice',
    'badge',
    'variantOption',
    'variantSelect',
    'addToCartButton',
    'addToCartButtonText',
  ];

  /** @type {number[]} */
  #tierPercents = [10, 15, 20];

  /** @type {number} */
  #cartQuantity = 0;

  /** @type {Set<string>} */
  #eligibleProductIds = new Set();

  /** @type {BundleProduct | null} */
  #activeProduct = null;

  /** @type {BundleVariant | null} */
  #activeVariant = null;

  /** @type {MutationObserver | null} */
  #recommendationsObserver = null;

  /** @type {number | undefined} */
  #addedTimeout;

  connectedCallback() {
    super.connectedCallback();

    const percents = (this.dataset.tierPercents || '')
      .split(',')
      .map((value) => parseInt(value, 10))
      .filter((value) => !isNaN(value));
    if (percents.length === 3) this.#tierPercents = percents;

    // Read before the first #fetchCartQuantity() below, which filters on it.
    this.#eligibleProductIds = readBundleEligibleProductIds();

    this.addEventListener(/** @type {any} */ ('slideshow:select'), this.#onSlideSelect);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    this.#fetchCartQuantity().then(() => this.#selectInitialSlide());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#recommendationsObserver?.disconnect();
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
  }

  /**
   * With no eligible-product list on the page there's nothing to filter on, so
   * everything is treated as qualifying — the behavior this had before
   * eligibility was taken into account.
   * @param {string|number|null|undefined} productId
   * @returns {boolean}
   */
  #isEligible(productId) {
    if (this.#eligibleProductIds.size === 0) return true;
    return productId != null && this.#eligibleProductIds.has(String(productId));
  }

  /**
   * This component only exists in the DOM once the recommended products have
   * already loaded (it's created wholesale by the Section Rendering API swap in
   * <product-recommendations>, which replaces its own innerHTML in one synchronous
   * operation) — so by the time connectedCallback runs, the first slide is already
   * a descendant of `this`. Select it immediately so the details panel isn't blank
   * on first paint. Falls back to a brief MutationObserver in case some other
   * rendering path (e.g. the theme editor) creates this element before its slides.
   */
  #selectInitialSlide() {
    const trySelectFirstSlide = () => {
      const firstSlide = this.querySelector('slideshow-slide');
      if (!firstSlide) return false;

      this.#selectSlide(firstSlide);
      return true;
    };

    if (trySelectFirstSlide()) return;

    this.#recommendationsObserver = new MutationObserver(() => {
      if (trySelectFirstSlide()) {
        this.#recommendationsObserver?.disconnect();
        this.#recommendationsObserver = null;
      }
    });
    this.#recommendationsObserver.observe(this, { childList: true, subtree: true });
  }

  /** @param {Event} event */
  #onSlideSelect = (event) => {
    const detail = /** @type {{ slide?: Element }} */ (/** @type {CustomEvent} */ (event).detail);
    if (!(detail?.slide instanceof Element)) return;

    this.#selectSlide(detail.slide);
  };

  /** @param {Element} slideElement */
  #selectSlide(slideElement) {
    for (const slide of this.querySelectorAll('.bundle-carousel__slide')) {
      slide.classList.toggle('is-selected', slide === slideElement);
    }

    const dataScript = slideElement.querySelector('.bundle-carousel__product-data');
    if (!dataScript?.textContent) return;

    try {
      this.#activeProduct = JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('Bundle & Save: failed to parse product data', error);
      return;
    }

    if (!this.#activeProduct) return;

    this.#activeVariant =
      this.#activeProduct.variants.find((variant) => variant.available) ?? this.#activeProduct.variants[0] ?? null;

    this.#renderDetails();
  }

  #onCartUpdate = () => {
    this.#fetchCartQuantity().then(() => this.#renderPrice());
  };

  async #fetchCartQuantity() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      // Reflects only what's actually in the cart right now — the current
      // product is not assumed to be included. Adding it to cart fires
      // ThemeEvents.cartUpdate, which triggers #onCartUpdate to refetch and
      // recompute the tier automatically.
      //
      // Only lines the Bundle & Save discounts cover are counted: adding an
      // ineligible item (a Flow Star, say) must not move the tier, since the
      // real automatic discount ignores it too.
      this.#cartQuantity =
        this.#eligibleProductIds.size === 0
          ? cart.item_count ?? 0
          : (cart.items ?? []).reduce(
              (total, item) => (this.#isEligible(item?.product_id) ? total + (item?.quantity ?? 0) : total),
              0
            );
    } catch (error) {
      this.#cartQuantity = 0;
    }
  }

  /** @returns {number} */
  #getTierPercent() {
    // A product the discounts don't cover never previews a bundle price, no
    // matter what's in the cart.
    if (!this.#isEligible(this.#activeProduct?.id)) return 0;

    const quantity = this.#cartQuantity;

    if (quantity >= 3) return this.#tierPercents[2] ?? 0;
    if (quantity === 2) return this.#tierPercents[1] ?? 0;
    if (quantity >= 1) return this.#tierPercents[0] ?? 0;

    return 0;
  }

  #renderDetails() {
    const product = this.#activeProduct;
    if (!product) return;

    const { title, variantOption, variantSelect } = this.refs;

    title.textContent = product.title;
    title.setAttribute('href', product.url);

    if (this.refs.description) {
      this.refs.description.innerHTML = product.description || '';
    }

    if (this.refs.sizeChart) {
      this.refs.sizeChart.innerHTML = product.sizeChartHtml || '';
    }

    if (this.refs.sizeChartWrapper) {
      this.refs.sizeChartWrapper.hidden = !product.hasSizeChart;
    }

    if (product.variants.length > 1) {
      variantSelect.innerHTML = product.variants
        .map((variant) => {
          const label = variant.available
            ? variant.title
            : `${variant.title} - ${this.dataset.soldOutText ?? 'Sold out'}`;
          const selected = variant.id === this.#activeVariant?.id ? 'selected' : '';
          const disabled = variant.available ? '' : 'disabled';

          return `<option value="${variant.id}" ${selected} ${disabled}>${label}</option>`;
        })
        .join('');
      variantOption.hidden = false;
    } else {
      variantOption.hidden = true;
    }

    this.#renderPrice();
    this.#updateAddToCartState();
  }

  #renderPrice() {
    const variant = this.#activeVariant;
    if (!variant) return;

    const { price, comparePrice, badge } = this.refs;
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const tierPercent = this.#getTierPercent();

    if (tierPercent > 0) {
      const discountedPrice = Math.round(variant.price * (1 - tierPercent / 100));

      price.textContent = formatMoney(discountedPrice, format, currency);
      comparePrice.textContent = formatMoney(variant.price, format, currency);
      comparePrice.hidden = false;
      badge.hidden = false;
    } else {
      price.textContent = formatMoney(variant.price, format, currency);

      if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        comparePrice.textContent = formatMoney(variant.compareAtPrice, format, currency);
        comparePrice.hidden = false;
      } else {
        comparePrice.hidden = true;
      }

      badge.hidden = true;
    }
  }

  #updateAddToCartState() {
    const { addToCartButton, addToCartButtonText } = this.refs;
    const canAddToCart = Boolean(this.#activeVariant?.available);

    addToCartButton.disabled = !canAddToCart;
    addToCartButtonText.textContent = canAddToCart
      ? this.dataset.addToCartText || 'Add to cart'
      : this.dataset.soldOutText || 'Sold out';
  }

  /** @param {Event} event */
  handleVariantChange(event) {
    const target = /** @type {HTMLSelectElement} */ (event.target);
    const variantId = Number(target.value);
    const variant = this.#activeProduct?.variants.find((v) => v.id === variantId);
    if (!variant) return;

    this.#activeVariant = variant;
    this.#renderPrice();
    this.#updateAddToCartState();
  }

  /**
   * Advances a product's image to the next one, wrapping back to the first after
   * the last (an endless click-through carousel). Works identically on mobile and
   * desktop since it's click-driven rather than hover-driven. Also selects this
   * product as the active one (so the price/variant/add-to-cart panel below reflects
   * whichever product's image was just clicked, not just whichever the customer
   * previously navigated to with the arrows).
   * @param {Event} event
   */
  handleImageClick(event) {
    // Use event.target, not event.currentTarget: the framework's delegated listener
    // is attached at the document level, so currentTarget would resolve to document.
    // event.target is rebound by the framework to the element that matched on:click.
    const viewport = /** @type {HTMLElement | null} */ (event.target);
    if (!viewport) return;

    const images = /** @type {HTMLElement[]} */ (Array.from(viewport.querySelectorAll('.bundle-carousel__image')));
    if (images.length > 1) {
      const currentIndex = images.findIndex((image) => image.classList.contains('is-active-image'));
      const nextIndex = (Math.max(currentIndex, 0) + 1) % images.length;

      images[currentIndex]?.classList.remove('is-active-image');
      images[nextIndex]?.classList.add('is-active-image');
    }

    // Ask the outer product carousel to select this slide. That scrolls it into
    // view if it was a peeking neighbor, and dispatches slideshow:select, which
    // #onSlideSelect already listens for to refresh the details panel — so no
    // need to duplicate that logic here. A click on the already-active slide is a
    // harmless no-op on the Slideshow's side (it only reselects on change).
    const slideElement = viewport.closest('.bundle-carousel__slide');
    const slideshowEl = /** @type {any} */ (viewport.closest('slideshow-component'));
    if (slideElement && typeof slideshowEl?.select === 'function') {
      const index = Array.from(slideshowEl.slides ?? []).indexOf(slideElement);
      if (index >= 0) slideshowEl.select(index, event);
    }
  }

  /** @param {Event} event */
  async handleAddToCart(event) {
    event.preventDefault();

    const variant = this.#activeVariant;
    if (!variant?.available) return;

    const { addToCartButton, addToCartButtonText } = this.refs;
    addToCartButton.disabled = true;

    try {
      const response = await fetch(/** @type {any} */ (Theme).routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const result = await response.json();

      if (result.status) {
        addToCartButtonText.textContent = result.message || 'Error';
        addToCartButton.disabled = false;
        return;
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.id || 'bundle-and-save-v2', {
          source: 'bundle-and-save-v2',
          itemCount: 1,
          sections: result.sections,
        })
      );

      addToCartButtonText.textContent = this.dataset.addedText || 'Added';

      if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
      this.#addedTimeout = window.setTimeout(() => {
        this.#updateAddToCartState();
      }, 2000);

      await this.#fetchCartQuantity();
      this.#renderPrice();
    } catch (error) {
      console.error('Bundle & Save: add to cart failed', error);
      addToCartButton.disabled = false;
    }
  }
}

if (!customElements.get('bundle-and-save-v2-component')) {
  customElements.define('bundle-and-save-v2-component', BundleAndSaveV2Component);
}